import { useState, useEffect, useCallback } from "react";
import { crDate, crDateTime } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import {
  Megaphone, Loader2, Sparkles, Copy, Trash2, Facebook, Instagram, Linkedin,
  Twitter, Mail, Film, ExternalLink, CheckCircle2, Clock, AlertCircle, RotateCw, Users2,
} from "lucide-react";

const CHANNEL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  email: Mail,
  reels: Film,
};

export default function Distribution() {
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openKit, setOpenKit] = useState(null);
  const [form, setForm] = useState({
    title: "",
    url: "",
    summary: "",
    campus: "general",
    tone: "warm",
    language: lang === "en" ? "en" : "es",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/distribution-kits");
      setKits(r.data.items || []);
    } catch {
      toast.error(t("distribution_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Poll pending kits every 5s until they become "ready" or "failed"
  useEffect(() => {
    const hasPending = kits.some((k) => k.status === "pending");
    if (!hasPending) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [kits, load]);

  const create = async () => {
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error(t("distribution_form_required"));
      return;
    }
    setCreating(true);
    try {
      await api.post("/distribution-kits/generate", {
        title: form.title.trim(),
        url: form.url.trim() || null,
        summary: form.summary.trim(),
        campus: form.campus === "general" ? null : form.campus,
        tone: form.tone,
        language: form.language,
      });
      toast.success(t("distribution_kit_queued"));
      setShowNew(false);
      setForm({ title: "", url: "", summary: "", campus: "general", tone: "warm", language: lang === "en" ? "en" : "es" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setCreating(false);
    }
  };

  const removeKit = async (id, title) => {
    const ok = await confirm({
      title: "¿Borrar kit de distribución?",
      description: `Se eliminará "${title || 'este kit'}" y no se puede deshacer.`,
      confirmText: "Sí, borrar",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/distribution-kits/${id}`);
      await load();
      setOpenKit(null);
      toast.success(t("deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="distribution-page">
      <PageHeader
        title={t("distribution_title")}
        subtitle={t("distribution_subtitle")}
        icon={Megaphone}
        action={
          <Button
            onClick={() => setShowNew(true)}
            data-testid="distribution-new-btn"
            className="btn-brand"
          >
            <Sparkles className="h-4 w-4" />
            {t("distribution_new_kit")}
          </Button>
        }
      />

      {loading ? (
        <div className="p-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : kits.length === 0 ? (
        <EmptyState onNew={() => setShowNew(true)} t={t} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kits.map((k) => (
            <KitCard key={k.id} kit={k} onOpen={() => setOpenKit(k)} t={t} />
          ))}
        </div>
      )}

      {/* New Kit Dialog */}
      <NewKitDialog
        open={showNew}
        onOpenChange={setShowNew}
        form={form}
        setForm={setForm}
        onSubmit={create}
        submitting={creating}
        t={t}
      />

      {/* Kit Detail Dialog */}
      {openKit && (
        <KitDetailDialog
          kit={openKit}
          onClose={() => setOpenKit(null)}
          onDelete={() => removeKit(openKit.id, openKit.title)}
          onRefresh={load}
          t={t}
        />
      )}
    </div>
  );
}

// -------- Empty State --------
function EmptyState({ onNew, t }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-slate-200 p-12 text-center" data-testid="distribution-empty">
      <div className="inline-flex p-3 rounded-full bg-brand-blue/10 text-brand-blue mb-3">
        <Megaphone className="h-6 w-6" />
      </div>
      <div className="font-semibold text-slate-900">{t("distribution_empty_title")}</div>
      <p className="text-sm text-slate-500 mt-1 mb-4 max-w-md mx-auto">
        {t("distribution_empty_body")}
      </p>
      <Button onClick={onNew} className="btn-brand" data-testid="distribution-empty-new">
        <Sparkles className="h-4 w-4" />
        {t("distribution_new_kit")}
      </Button>
    </div>
  );
}

// -------- Kit Card --------
function KitCard({ kit, onOpen, t }) {
  const statusBadge = () => {
    if (kit.status === "pending") return <Badge className="bg-blue-100 text-blue-800"><Clock className="h-3 w-3 mr-1" /> {t("distribution_status_pending")}</Badge>;
    if (kit.status === "failed")  return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" /> {t("distribution_status_failed")}</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" /> {t("distribution_status_ready")}</Badge>;
  };
  const published = (kit.published_channels || []).length;
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onOpen}
      data-testid={`kit-card-${kit.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base line-clamp-2">{kit.title}</CardTitle>
          {statusBadge()}
        </div>
        <CardDescription className="line-clamp-2 text-xs mt-1">{kit.summary}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{crDate(kit.created_at)}</span>
          {kit.status === "ready" && (
            <span className="font-medium">
              {published} / 6 {t("distribution_channels_published")}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// -------- New Kit Dialog --------
function NewKitDialog({ open, onOpenChange, form, setForm, onSubmit, submitting, t }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("distribution_new_kit_title")}</DialogTitle>
          <DialogDescription>{t("distribution_new_kit_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("distribution_form_title")} *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="10 Spanish phrases for the Feria"
              data-testid="kit-form-title"
              className="mt-1"
            />
          </div>
          <div>
            <Label>{t("distribution_form_url")}</Label>
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://wespeakspanish-cr.com/blog/..."
              data-testid="kit-form-url"
              className="mt-1"
            />
          </div>
          <div>
            <Label>{t("distribution_form_summary")} *</Label>
            <Textarea
              rows={4}
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder={t("distribution_form_summary_placeholder")}
              data-testid="kit-form-summary"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{t("distribution_form_campus")}</Label>
              <Select value={form.campus} onValueChange={(v) => setForm({ ...form, campus: v })}>
                <SelectTrigger className="mt-1" data-testid="kit-form-campus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t("lead_campus_general")}</SelectItem>
                  <SelectItem value="santa_ana">Santa Ana</SelectItem>
                  <SelectItem value="atenas">Atenas</SelectItem>
                  <SelectItem value="grecia">Grecia</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("distribution_form_tone")}</Label>
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warm">{t("distribution_tone_warm")}</SelectItem>
                  <SelectItem value="professional">{t("distribution_tone_pro")}</SelectItem>
                  <SelectItem value="humorous">{t("distribution_tone_fun")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("distribution_form_language")}</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2">
            <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{t("distribution_generation_notice")}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t("cancel")}</Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || !form.title.trim() || !form.summary.trim()}
            data-testid="kit-form-submit"
            className="btn-brand"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {t("distribution_generate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Kit Detail Dialog --------
function KitDetailDialog({ kit, onClose, onDelete, onRefresh, t }) {
  const [current, setCurrent] = useState(kit);
  // Auto-refresh detail when pending
  useEffect(() => {
    setCurrent(kit);
  }, [kit]);
  useEffect(() => {
    if (current.status !== "pending") return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/distribution-kits/${current.id}`);
        setCurrent(r.data);
        if (r.data.status !== "pending") {
          onRefresh?.();
          clearInterval(iv);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [current.id, current.status, onRefresh]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} — ${t("distribution_copied")}`);
    } catch { toast.error("No se pudo copiar"); }
  };

  const togglePublished = async (channel) => {
    const isPub = (current.published_channels || []).includes(channel);
    try {
      const r = await api.post(`/distribution-kits/${current.id}/mark-published`, {
        channel,
        published: !isPub,
      });
      setCurrent({ ...current, published_channels: r.data.published_channels });
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const isPub = (ch) => (current.published_channels || []).includes(ch);
  const c = current.content;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-8">{current.title}</DialogTitle>
          <DialogDescription>{current.summary}</DialogDescription>
        </DialogHeader>

        {current.status === "pending" && (
          <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <div className="font-semibold text-slate-900">{t("distribution_pending_title")}</div>
            <p className="text-sm text-slate-600 mt-1">{t("distribution_pending_body")}</p>
          </div>
        )}
        {current.status === "failed" && (
          <div className="rounded-lg border-2 border-red-200 bg-red-50 p-6">
            <div className="font-semibold text-red-900 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {t("distribution_failed_title")}</div>
            <p className="text-sm text-red-800 mt-1">{current.error || t("distribution_failed_body")}</p>
          </div>
        )}
        {current.status === "ready" && c && (
          <div className="space-y-5">
            {/* Facebook variants */}
            <ChannelSection
              icon={Facebook}
              color="bg-blue-600"
              title={t("distribution_fb_variants")}
              published={isPub("facebook")}
              onTogglePublished={() => togglePublished("facebook")}
              t={t}
            >
              <div className="space-y-2">
                {c.facebook_variants?.map((v, i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-white p-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      {v.target}
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{v.copy}</div>
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => copy(v.copy, "Facebook")}>
                      <Copy className="h-3 w-3" /> {t("copy")}
                    </Button>
                  </div>
                ))}
              </div>
            </ChannelSection>

            {/* Instagram */}
            <ChannelSection
              icon={Instagram}
              color="bg-gradient-to-br from-purple-500 to-pink-500"
              title={t("distribution_instagram")}
              published={isPub("instagram")}
              onTogglePublished={() => togglePublished("instagram")}
              t={t}
            >
              <div className="rounded-md border border-slate-200 bg-white p-3">
                <div className="text-sm whitespace-pre-wrap text-slate-800">{c.instagram_post?.caption}</div>
                {c.instagram_post?.hashtags?.length > 0 && (
                  <div className="mt-2 text-xs text-blue-600 font-medium break-words">
                    {c.instagram_post.hashtags.join(" ")}
                  </div>
                )}
                <Button size="sm" variant="outline" className="mt-2" onClick={() => copy(`${c.instagram_post.caption}\n\n${(c.instagram_post.hashtags || []).join(" ")}`, "Instagram")}>
                  <Copy className="h-3 w-3" /> {t("copy")}
                </Button>
              </div>
            </ChannelSection>

            {/* LinkedIn */}
            <ChannelSection
              icon={Linkedin}
              color="bg-sky-700"
              title={t("distribution_linkedin")}
              published={isPub("linkedin")}
              onTogglePublished={() => togglePublished("linkedin")}
              t={t}
            >
              <div className="rounded-md border border-slate-200 bg-white p-3">
                <div className="text-sm whitespace-pre-wrap text-slate-800">{c.linkedin_post}</div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => copy(c.linkedin_post, "LinkedIn")}>
                  <Copy className="h-3 w-3" /> {t("copy")}
                </Button>
              </div>
            </ChannelSection>

            {/* Twitter thread */}
            <ChannelSection
              icon={Twitter}
              color="bg-slate-900"
              title={t("distribution_twitter")}
              published={isPub("twitter")}
              onTogglePublished={() => togglePublished("twitter")}
              t={t}
            >
              <div className="space-y-2">
                {c.twitter_thread?.map((tweet, i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-white p-2.5">
                    <div className="text-xs font-semibold text-slate-500 mb-1">Tweet {i + 1} / {c.twitter_thread.length}</div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{tweet}</div>
                    <Button size="sm" variant="outline" className="mt-1.5 h-7 text-xs" onClick={() => copy(tweet, `Tweet ${i + 1}`)}>
                      <Copy className="h-3 w-3" /> {t("copy")}
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => copy(c.twitter_thread.join("\n\n---\n\n"), "Full thread")}>
                  <Copy className="h-3 w-3" /> {t("distribution_copy_full_thread")}
                </Button>
              </div>
            </ChannelSection>

            {/* Email */}
            <ChannelSection
              icon={Mail}
              color="bg-emerald-600"
              title={t("distribution_email")}
              published={isPub("email")}
              onTogglePublished={() => togglePublished("email")}
              t={t}
            >
              <div className="rounded-md border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">{t("distribution_email_subject")}</div>
                <div className="text-sm font-semibold text-slate-900 mb-3">{c.email_subject}</div>
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">{t("distribution_email_body")}</div>
                <div className="text-sm text-slate-800 border border-slate-100 rounded p-2 max-h-[220px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: c.email_body_html }} />
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => copy(c.email_subject, "Subject")}>
                    <Copy className="h-3 w-3" /> {t("distribution_copy_subject")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => copy(c.email_body_html, "Email HTML")}>
                    <Copy className="h-3 w-3" /> {t("distribution_copy_body")}
                  </Button>
                  <SendNewsletterButton kit={current} onSent={onRefresh} t={t} />
                </div>
                {current.newsletter_sent_at && (
                  <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3" />
                    {t("newsletter_last_sent")
                      .replace("{sent}", current.newsletter_sent_count || 0)
                      .replace("{when}", crDateTime(current.newsletter_sent_at))}
                    {current.newsletter_failed_count > 0 && (
                      <span className="text-amber-700 ml-1">
                        ({current.newsletter_failed_count} {t("newsletter_failed")})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </ChannelSection>

            {/* Reels ideas */}
            <ChannelSection
              icon={Film}
              color="bg-rose-600"
              title={t("distribution_reels")}
              published={isPub("reels")}
              onTogglePublished={() => togglePublished("reels")}
              t={t}
            >
              <div className="space-y-2">
                {c.reels_ideas?.map((r, i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-white p-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {t("distribution_reels_hook")} {i + 1}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-1">{r.hook}</div>
                    <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.script}</div>
                    <Button size="sm" variant="outline" className="mt-1.5 h-7 text-xs"
                      onClick={() => copy(`${r.hook}\n\n${r.script}`, `Reel ${i + 1}`)}>
                      <Copy className="h-3 w-3" /> {t("copy")}
                    </Button>
                  </div>
                ))}
              </div>
            </ChannelSection>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onDelete} className="text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 className="h-4 w-4" /> {t("delete")}
          </Button>
          <Button onClick={onClose}>{t("done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Send Newsletter Button + Dialog --------
function SendNewsletterButton({ kit, onSent, t }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("leads");
  const [statuses, setStatuses] = useState(["new", "contacted", "later"]);
  const [testEmail, setTestEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(`/distribution-kits/${kit.id}/preview-recipients`, {
        audience,
        campus: kit.campus || null,
        statuses: audience === "leads" ? statuses : null,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setPreviewing(false);
    }
  };

  const send = async (isTest) => {
    if (isTest && !testEmail.trim()) {
      toast.error(t("newsletter_test_email_required"));
      return;
    }
    if (!isTest && !window.confirm(t("newsletter_confirm_send").replace("{count}", preview?.total || "?"))) return;
    setSending(true);
    try {
      await api.post(`/distribution-kits/${kit.id}/send-newsletter`, {
        audience,
        campus: kit.campus || null,
        statuses: audience === "leads" ? statuses : null,
        test_email: isTest ? testEmail.trim() : null,
      });
      toast.success(isTest ? t("newsletter_test_queued") : t("newsletter_queued"));
      setOpen(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  const toggleStatus = (s) => {
    setStatuses((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
    setPreview(null);
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => { setOpen(true); setPreview(null); }}
        disabled={kit.newsletter_sending}
        data-testid="newsletter-send-btn"
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
      >
        {kit.newsletter_sending
          ? <><Loader2 className="h-3 w-3 animate-spin" /> {t("newsletter_sending")}</>
          : <><Mail className="h-3 w-3" /> {t("newsletter_send_button")}</>
        }
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("newsletter_dialog_title")}</DialogTitle>
            <DialogDescription>{t("newsletter_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("newsletter_audience")}</Label>
              <Select value={audience} onValueChange={(v) => { setAudience(v); setPreview(null); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leads">{t("newsletter_audience_leads")}</SelectItem>
                  <SelectItem value="students">{t("newsletter_audience_students")}</SelectItem>
                  <SelectItem value="both">{t("newsletter_audience_both")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {audience !== "students" && (
              <div>
                <Label className="mb-2 block">{t("newsletter_lead_statuses")}</Label>
                <div className="flex flex-wrap gap-1.5">
                  {["new", "contacted", "interview_meeting", "send_info", "info_ready", "later", "not_interested"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleStatus(s)}
                      className={`text-xs px-2.5 py-1 rounded-full border ${
                        statuses.includes(s)
                          ? "bg-brand-blue text-white border-brand-blue"
                          : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {t(`lead_status_${s}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {kit.campus && (
              <div className="text-xs text-slate-500">
                {t("newsletter_campus_filter")}: <span className="font-semibold text-slate-800">{t(`lead_campus_${kit.campus}`)}</span>
              </div>
            )}

            <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <Button size="sm" variant="outline" onClick={doPreview} disabled={previewing} className="mb-2">
                {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users2 className="h-3 w-3" />}
                {t("newsletter_preview_btn")}
              </Button>
              {preview !== null && (
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">
                    {t("newsletter_preview_total").replace("{count}", preview.total)}
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                      {preview.sample.slice(0, 5).map((r, i) => (
                        <div key={i}>{r.name} — <span className="text-slate-400">{r.email}</span></div>
                      ))}
                      {preview.total > 5 && <div className="italic">+ {preview.total - 5} {t("more")}</div>}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-slate-100">
              <Label className="text-xs text-slate-600">{t("newsletter_test_email_label")}</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@ejemplo.com"
                  className="text-sm"
                />
                <Button size="sm" variant="outline" onClick={() => send(true)} disabled={sending || !testEmail.trim()}>
                  {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : t("newsletter_send_test")}
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{t("newsletter_test_hint")}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button
              onClick={() => send(false)}
              disabled={sending || !preview || preview.total === 0}
              data-testid="newsletter-send-real-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {preview
                ? t("newsletter_send_to_count").replace("{count}", preview.total)
                : t("newsletter_send_button")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// -------- Section wrapper with "published" checkbox --------
function ChannelSection({ icon: Icon, color, title, children, published, onTogglePublished, t }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-md text-white ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="font-semibold text-slate-900 flex-1">{title}</div>
        <button
          onClick={onTogglePublished}
          className={`text-xs px-2 py-1 rounded-md border transition-colors ${
            published
              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
          data-testid={`toggle-published-${title}`}
        >
          {published ? (
            <><CheckCircle2 className="h-3 w-3 mr-1 inline-block" /> {t("distribution_marked_published")}</>
          ) : (
            <>{t("distribution_mark_published")}</>
          )}
        </button>
      </div>
      {children}
    </div>
  );
}
